<template>
  <div id="programme-schedule" class="content-with-spacing">
    <div class="programme-header">
      <div class="title-container">
        <h1 v-if="programmeDetails">
          <i class="fas fa-chart-bar"></i> {{ programmeDetails.name }}
        </h1>
      </div>
      <div v-if="userProgrammes.length > 1" class="header-dropdown">
        <span class="dropdown-label">Programmes:</span>
        <k-dropdown class="programme-dropdown" :options="userProgrammes" v-model="currentProgrammeId"></k-dropdown>
      </div>
    </div>
    <div v-if="scheduleReady" class="calendar-container panel panel-default">
      <k-calendar class="calendar-content"
        :schedule="scheduledEvents"
        :existingIcalSubscriptionId="icalSubscriptionId"
        :existingIcalSubscriptionUrl="icalSubscriptionUrl"
      ></k-calendar>
    </div>
  </div>
</template>

<style scoped>
.programme-header {
  display: flex;
}

.programme-header .header-dropdown {
  display: flex;
  align-items: center;
  gap: 15px;
}

.calendar-content {
  padding: 15px;
}
</style>

<script>
import useAppProgrammeStore from '@stores/app-programme-store';
import { storeToRefs } from 'pinia';
import TimeMixin from '@mixins/time-mixins';
import PageReadyMixin from '@mixins/page-ready-mixin';
import KDropdown from '@base-components/k-dropdown.vue';
import KCalendar from '@base-components/k_calendar/k-calendar.vue';

export default {
  components: {
    KDropdown,
    KCalendar,
  },

  mixins: [TimeMixin, PageReadyMixin],

  data() {
    const programmeStore = useAppProgrammeStore();
    const { userProgrammes, currentProgrammeId } = storeToRefs(programmeStore);
    return {
      programmeStore,
      userProgrammes,
      currentProgrammeId,
      schedule: [],
      scheduleReady: false,
      icalSubscriptionId: undefined,
      icalSubscriptionUrl: undefined,
    };
  },

  watch: {
    currentProgrammeId: {
      handler(newVal) {
        if (!newVal) return;
        const currentRouteProgrammeId = Number(this.$route.params.programmeId);
        if (newVal !== currentRouteProgrammeId) {
          this.$router.replace({ name: 'programme_schedule', params: { programmeId: this.programmeStore.currentProgrammeId } });
        }
      },
    },
    '$route.params.programmeId': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          if (this.programmeStore.isProgrammeAccessible(newVal)) {
            this.programmeStore.currentProgrammeId = Number(newVal);
            this.getUserProgrammeSchedule();
          } else {
            this.programmeStore.currentProgrammeId = null;
            this.$router.push({ name: '404' });
          }
        }
      },
      immediate: true,
    },
  },

  computed: {
    programmeDetails() {
      return this.programmeStore.getProgramme(this.programmeStore.currentProgrammeId);
    },
    scheduledEvents() {
      return this.schedule.filter(x => x.start_time).map(x => ({
        title: x.asset_name || x.module_name,
        start: this.parseTimestamp(x.start_time, undefined, 'yyyy-MM-dd HH:mm'),
        end: this.parseTimestamp(x.end_time, undefined, 'yyyy-MM-dd HH:mm'),
        id: `${x.event_type}_${x.module_asset_id || x.module_id}`,
        module_id: x.module_id,
        event_type: x.event_type,
        classNames: [x.event_type],
        extendedProps: {
          module_asset_id: x.module_asset_id,
          module_name: x.module_name,
          module_release_date: x.module_release_date,
          is_dot_event: this.isDotEvent(x),
          module_expected_completion_date: x.module_expected_completion_date,
        },
      }));
    },
  },

  methods: {
    isDotEvent(ev) {
      return (this.parseTimestamp(ev.start_time, undefined, 'yyyy-MM-dd')
      === this.parseTimestamp(ev.end_time, undefined, 'yyyy-MM-dd') || ev.event_type === 'meeting') || !ev.end_time;
    },
    getUserProgrammeSchedule() {
      this.$Loading.start();
      this.scheduleReady = false;
      this.$logger.info('Getting programme schedule');
      this.$http.get(`/api/curriculum/programmes/${this.programmeStore.currentProgrammeId}/schedule`)
        .then(response => {
          this.schedule = response.data.schedule;
          this.icalSubscriptionId = response.data.ical_subscription_id;
          this.icalSubscriptionUrl = response.data.ical_url;
          this.$logger.info('Got programme schedule', undefined);
        })
        .catch(err => {
          this.schedule = [];
          this.icalSubscriptionId = null;
          this.icalSubscriptionUrl = null;
          this.$logger.autowarn('Error getting user programme schedule', { programmeId: this.programmeStore.currentProgrammeId }, err);
          this.$ktoast.info('No schedule found for this programme');
        }).then(() => {
          this.scheduleReady = true;
          this.$Loading.finish();
        });
    },
  },
};
</script>
